"use client"

import type { ProductGroupItem } from "@/app/(site)/_components/Organisms/ProductGrids/ProductGridContent"
import type { FilterState } from "@/app/(site)/_components/Organisms/ProductGrids/filterReducer"
import { trackProductListFromGrid } from "@/app/(site)/_components/Organisms/ProductGrids/tracking-helpers"
import type { SiteContext } from "@/lib/utils/tracking/siteContext"
import { useEffect, useMemo, useRef } from "react"

type UseProductGridTrackingParams = {
    products: ProductGroupItem[]
    filterState: FilterState
    isLoading: boolean
    externalId: string | undefined
    categoryPath: string
    listHierarchy: string
    siteContext: SiteContext
    locale: string
}

export function useProductGridTracking({
    products,
    filterState,
    isLoading,
    externalId,
    categoryPath,
    listHierarchy,
    siteContext,
    locale
}: UseProductGridTrackingParams) {
    const previousCategory = useRef(categoryPath)
    const lastTrackedProductState = useRef<string>("")
    const lastTrackedFilterState = useRef<string>("")
    const previousProductCountForPagination = useRef(0)

    const productListKey = useMemo(
        () => `${externalId}|${products.map(g => g.key).join(",")}`,
        [products, externalId]
    )

    const sortKey = filterState.sort ? JSON.stringify(filterState.sort) : ""
    const filterKey =
        filterState.filters.length > 0 ? JSON.stringify(filterState.filters) : ""

    useEffect(() => {
        if (previousCategory.current !== categoryPath) {
            previousCategory.current = categoryPath
            lastTrackedProductState.current = ""
            lastTrackedFilterState.current = ""
            previousProductCountForPagination.current = 0
        }

        if (!products || products.length === 0) return

        const listSortOrder = filterState.sort?.meta?.title || "Recommended"
        trackProductListFromGrid({
            extendedProducts: products,
            productListKey,
            sortKey,
            filterKey,
            listId: listHierarchy,
            listSortOrder,
            siteContext,
            listHierarchy,
            locale,
            lastTrackedProductStateRef: lastTrackedProductState,
            previousProductCountForPaginationRef: previousProductCountForPagination
        })

        if (filterState.filters.length > 0 && !isLoading) {
            const filterStateHash = JSON.stringify(filterState.filters)
            if (lastTrackedFilterState.current !== filterStateHash) {
                const filterResults = products.length
                import("@/lib/utils/tracking/gtmEvents").then(({ filterEvent }) => {
                    filterState.filters.forEach(filter => {
                        const filterValue = Array.isArray(filter.data)
                            ? filter.data.join(",")
                            : String(filter.data)
                        filterEvent(
                            filter.field,
                            filterValue,
                            siteContext,
                            listHierarchy,
                            listSortOrder,
                            filterResults
                        )
                    })
                })
                lastTrackedFilterState.current = filterStateHash
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        productListKey,
        sortKey,
        filterKey,
        products.length,
        siteContext.site_country,
        siteContext.site_language
    ])
}
